<script>
  import gql from 'graphql-tag'

  const TEMPLATE_FIELDS = `
    kind title subject body isDefault defaultSubject defaultBody
    reminderSubjectExample reminderNotice
    placeholders { key tag label description example subjectAllowed }
    preview { subject bodyHtml errors }
  `

  export default {
    props: ['gid', 'kind', 'title'],
    apollo: {
      template: {
        fetchPolicy: "no-cache",
        query: gql`query($gid: ID, $kind: EmailTemplateKindEnum!) {
          merchant(gid: $gid) { merchantUser { settings { emailTemplate(kind: $kind) { ${TEMPLATE_FIELDS} } } } }
        }`,
        variables() {
          return { gid: this.gid, kind: this.kind }
        },
        update(data) {
          const template = data.merchant.merchantUser.settings.emailTemplate
          // The panes render this in both modes; editing just replaces it with
          // the draft as the merchant types.
          this.preview = template.preview
          return template
        }
      }
    },

    data() {
      return {
        template: null,
        showForm: false,
        draft: { subject: '', body: '' },
        preview: { subject: '', bodyHtml: '', errors: [] },
        previewTimer: null,
        previewLoading: false,
        activeField: 'body',
        confirmReset: false,
        saving: false
      }
    },
    computed: {
      heading() {
        return this.title || (this.template && this.template.title) || 'Send a bill email'
      },
      // Array.from, not .length: String#length counts UTF-16 code units, so an
      // emoji scored 2 against a cap the server measures in characters and the
      // counter showed "289 / 150" for a subject Rails accepted as 150.
      subjectLength() {
        return this.draft.subject ? Array.from(this.draft.subject).length : 0
      },
      bodyLength() {
        return this.draft.body ? Array.from(this.draft.body).length : 0
      },
      placeholders() {
        return this.template ? this.template.placeholders : []
      },
      reminderNotice() {
        return this.template && this.template.reminderNotice
      },
      reminderSubject() {
        return this.template && this.template.reminderSubjectExample
      }
    },
    methods: {
      edit() {
        this.draft = { subject: this.template.subject, body: this.template.body }
        this.preview = Object.assign({}, this.preview, {errors: []})
        this.confirmReset = false
        this.showForm = true
        this.schedulePreview()
      },
      close() {
        clearTimeout(this.previewTimer)
        this.showForm = false
        this.confirmReset = false
        this.$apollo.queries.template.refetch()
      },
      chipDisabled(placeholder) {
        return this.activeField === 'subject' && !placeholder.subjectAllowed
      },
      insert(placeholder) {
        if (this.chipDisabled(placeholder)) return

        const field = this.activeField
        const el = this.$refs[field]
        const text = this.draft[field] || ''
        const start = el ? el.selectionStart : text.length
        const end = el ? el.selectionEnd : text.length
        const tag = placeholder.tag

        this.draft[field] = text.slice(0, start) + tag + text.slice(end)
        this.$nextTick(() => {
          if (!el) return
          el.focus()
          el.setSelectionRange(start + tag.length, start + tag.length)
        })
        this.schedulePreview()
      },
      schedulePreview() {
        clearTimeout(this.previewTimer)
        this.previewTimer = setTimeout(this.fetchPreview, 300)
      },
      fetchPreview() {
        this.previewLoading = true
        this.$apollo.query({
          query: gql`query($gid: ID, $kind: EmailTemplateKindEnum!, $subject: String, $body: String) {
            merchant(gid: $gid) { merchantUser { settings { emailTemplate(kind: $kind) {
              preview(subject: $subject, body: $body) { subject bodyHtml errors }
            } } } }
          }`,
          variables: { gid: this.gid, kind: this.kind, subject: this.draft.subject, body: this.draft.body },
          fetchPolicy: 'no-cache'
        }).then(result => {
          this.preview = result.data.merchant.merchantUser.settings.emailTemplate.preview
          this.previewLoading = false
        }, error => {
          this.previewLoading = false
          this.gqlErrorHandler(error)
        })
      },
      save() {
        this.saving = true
        this.$apollo.mutate({
          mutation: gql`mutation($kind: EmailTemplateKindEnum!, $subject: String!, $body: String!) {
            updateEmailTemplate(input: {kind: $kind, subject: $subject, body: $body}) {
              emailTemplate { ${TEMPLATE_FIELDS} }
              errors
            }
          }`,
          variables: { kind: this.kind, subject: this.draft.subject, body: this.draft.body }
        }).then(result => {
          this.saving = false
          const payload = result.data.updateEmailTemplate
          if (payload.errors.length) {
            this.preview.errors = payload.errors
            return
          }
          this.template = payload.emailTemplate
          // Same reason as in reset(). Correct here only by luck today, because
          // fetchPreview() has already set `preview` from the draft just saved.
          this.preview = this.template.preview
          this.showForm = false
          this.notifySuccess("Email template updated")
        }, error => {
          this.saving = false
          this.gqlErrorHandler(error)
        })
      },
      reset() {
        if (!this.confirmReset) {
          this.confirmReset = true
          return
        }
        this.$apollo.mutate({
          mutation: gql`mutation($kind: EmailTemplateKindEnum!) {
            resetEmailTemplate(input: {kind: $kind}) {
              emailTemplate { ${TEMPLATE_FIELDS} }
              errors
            }
          }`,
          variables: { kind: this.kind }
        }).then(result => {
          // The panes read `preview`, not `template.preview`, and assigning
          // `template` here does not run the query's update() hook. Without this
          // the merchant keeps seeing their old wording until a refetch.
          this.template = result.data.resetEmailTemplate.emailTemplate
          this.preview = this.template.preview
          this.confirmReset = false
          this.showForm = false
          this.notifySuccess("Email template reset to default")
        }, error => {
          this.confirmReset = false
          this.gqlErrorHandler(error)
        })
      }
    }
  }
</script>

<template>
  <div class="mail-setting">
    <h6 class="mail-setting__title">{{ heading }}</h6>
    <p class="mail-setting__hint">
      Your customer gets two emails: the bill, and a reminder a day later if it has not been
      paid. Both carry the words you write here. The reminder keeps its own subject line and
      opening sentence, so it reads as a nudge rather than a second bill.
      <span class="mail-setting__state" v-if="template">
        {{ template.isDefault ? 'Using the standard wording.' : 'You have edited this.' }}
      </span>
    </p>

    <div class="mail-setting__loading" v-if="$apollo.loading && !template">
      <i class="fas fa-spinner fa-spin"></i> Loading
    </div>

    <template v-else-if="template">
      <!-- The two mails as the customer receives them. Same device as the logo
           panes above: show the artifact, label it, put the controls underneath. -->
      <div class="mail-set">
        <section class="mail-pane">
          <div class="mail-pane__subject">{{ preview.subject }}</div>
          <div class="mail-pane__sheet" v-html="preview.bodyHtml"></div>
          <div class="mail-pane__meta">
            <span class="mail-pane__label">The bill</span>
            <span class="mail-pane__when">sent right away</span>
          </div>
        </section>

        <section class="mail-pane">
          <div class="mail-pane__subject">{{ reminderSubject }}</div>
          <div class="mail-pane__sheet">
            <p class="mail-pane__notice">{{ reminderNotice }}</p>
            <p class="mail-pane__same">Then the same message as the bill.</p>
          </div>
          <div class="mail-pane__meta">
            <span class="mail-pane__label">The reminder</span>
            <span class="mail-pane__when">a day later, if unpaid</span>
          </div>
        </section>
      </div>

      <ul class="mail-errors" v-if="preview.errors.length">
        <li v-for="(error, i) in preview.errors" :key="i">
          <i class="fas fa-exclamation-triangle"></i> {{ error }}
        </li>
      </ul>

      <div class="mail-setting__actions" v-if="!showForm">
        <button class="btn btn-grey" @click="edit">Edit wording</button>
      </div>

      <div class="mail-editor" v-if="showForm">
        <label class="mail-editor__label" for="mail-subject">Subject</label>
        <input
          id="mail-subject"
          type="text"
          ref="subject"
          v-model="draft.subject"
          @focus="activeField = 'subject'"
          @input="schedulePreview">
        <div class="mail-editor__count">{{ subjectLength }} / 150</div>

        <label class="mail-editor__label" for="mail-body">Message</label>
        <textarea
          id="mail-body"
          ref="body"
          class="mail-editor__body"
          v-model="draft.body"
          @focus="activeField = 'body'"
          @input="schedulePreview"></textarea>
        <div class="mail-editor__count">{{ bodyLength }} / 5000</div>

        <div class="mail-editor__fields">
          <span class="mail-editor__fields-label">Insert a detail</span>
          <span
            v-for="p in placeholders"
            :key="p.key"
            class="chip pointer"
            :class="{ 'is-disabled': chipDisabled(p) }"
            :title="p.description"
            @click="insert(p)">
            {{ p.label }}
          </span>
        </div>

        <!-- v-pre: this names a placeholder literally, and Vue would otherwise
             read the braces as its own interpolation and render nothing. -->
        <p class="mail-editor__note" v-pre>
          In the message you can use &lt;b&gt;, &lt;i&gt;, &lt;br&gt;, links written as
          &lt;a href="https://…"&gt; and images as &lt;img src="https://…" alt="…"&gt;.
          Anything else shows as the characters you typed, so nothing you write is lost.
          The subject is always plain text.
        </p>
        <p class="mail-editor__note" v-pre>
          A line whose details are all empty is left out, so "Invoice: {{invoice}}" disappears
          on a bill without one. The preview fills every detail, so it cannot show that.
        </p>

        <div class="mail-editor__actions">
          <button class="btn" :disabled="saving" @click="save">Save</button>
          <button class="btn-flat" @click="reset">
            {{ confirmReset ? 'Click again to confirm' : 'Reset to standard' }}
          </button>
          <button class="btn-flat right" @click="close">Close</button>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
  .mail-setting__title {
    margin: 0 0 4px;
    font-size: 15px;
    font-weight: 600;
    color: #242424;
  }
  .mail-setting__hint {
    margin: 0 0 18px;
    max-width: 52em;
    font-size: 13px;
    line-height: 1.5;
    color: #757575;
  }
  .mail-setting__state {
    color: #424242;
  }
  .mail-setting__loading {
    font-size: 13px;
    color: #9e9e9e;
  }

  /* Two mails, side by side, each shown the way the customer receives it. */
  .mail-set {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    /* stretch: the two are a pair and read better squared up, so the shorter
       reminder takes the bill's height rather than ending halfway up. */
    align-items: stretch;
    gap: 18px;
    max-width: 760px;
  }
  @media only screen and (max-width: 992px) {
    .mail-set {
      grid-template-columns: minmax(0, 1fr);
    }
  }

  .mail-pane {
    display: flex;
    flex-direction: column;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 3px;
    overflow: hidden;
  }
  .mail-pane__subject {
    padding: 10px 14px;
    background: #fff;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    font-size: 13px;
    font-weight: 600;
    color: #242424;
    overflow-wrap: anywhere;
  }
  /* The message itself, on the ground the mail client shows it on. White, as the
     link-order mails now are, so the logo's own white plate does not read as a
     box drawn around the logo. */
  .mail-pane__sheet {
    flex: 1;
    padding: 16px 14px;
    background: #fff;
    font-size: 13px;
    line-height: 1.5;
    color: #37474f;
    overflow-wrap: anywhere;
  }
  .mail-pane__notice {
    margin: 0 0 10px;
  }
  .mail-pane__same {
    margin: 0;
    color: #90a4ae;
    font-style: italic;
  }
  .mail-pane__meta {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 12px;
    background: #fafafa;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }
  .mail-pane__label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: #424242;
  }
  /* The label says which mail, this says when - two jobs, two elements. */
  .mail-pane__when {
    font-size: 11px;
    color: #9e9e9e;
  }

  .mail-pane__sheet ::v-deep table {
    width: auto;
  }
  .mail-pane__sheet ::v-deep tr {
    border-bottom: none;
  }
  .mail-pane__sheet ::v-deep td {
    padding: 0;
  }

  .mail-errors {
    margin: 12px 0 0;
    max-width: 760px;
    font-size: 13px;
    color: #c62828;
  }

  .mail-setting__actions,
  .mail-editor__actions {
    margin-top: 18px;
    max-width: 760px;
  }

  .mail-editor {
    margin-top: 22px;
    padding-top: 18px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    max-width: 760px;
  }
  .mail-editor__label {
    display: block;
    margin-bottom: 2px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: #424242;
  }
  .mail-editor__label:not(:first-child) {
    margin-top: 18px;
  }
  .mail-editor__count {
    font-size: 11px;
    color: #9e9e9e;
  }
  .mail-editor__body {
    min-height: 220px;
    padding: 8px;
    font-family: inherit;
    line-height: 1.5;
  }
  .mail-editor__fields {
    margin-top: 16px;
  }
  .mail-editor__fields-label {
    display: block;
    margin-bottom: 6px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: #424242;
  }
  .mail-editor__fields .chip {
    margin-bottom: 4px;
  }
  .mail-editor__fields .chip.is-disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .mail-editor__note {
    margin: 10px 0 0;
    font-size: 12px;
    line-height: 1.5;
    color: #9e9e9e;
  }
</style>
